import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Hero gallery — crossfade between stacked hero images.
 *
 * Markup comes from the Hero block when several images are selected:
 *   <div class="block__hero__gallery" data-hero-gallery>
 *     <div class="block__hero__slide" data-hero-gallery-active>…</div>
 *     <div class="block__hero__slide">…</div>
 *   </div>
 *
 * The transition: the current slide slides slightly LEFT while fading out, and
 * the incoming slide fades in while sliding in from the RIGHT. Both run at once
 * so the two images cross over each other.
 *
 * Slides are stacked inset:0, so CSS shows only the first one until GSAP takes
 * over — that keeps a no-JS/failed-JS page showing a valid hero rather than a
 * blank box or all images piled up.
 */
function initHeroGallery() {
  const hold = 5;        // Seconds each slide stays fully visible
  const duration = 1.2;  // Crossfade duration
  const shift = 4;       // How far slides travel, in % of the frame width

  document.querySelectorAll("[data-hero-gallery]").forEach((root) => {
    // Guard against double-init if called twice on the same DOM.
    if (root.hasAttribute("data-hero-gallery-initialized")) return;
    root.setAttribute("data-hero-gallery-initialized", "");

    const slides = Array.from(root.querySelectorAll(".block__hero__slide"));
    if (slides.length < 2) return;

    // Respect a user's reduced-motion preference: show the first slide and stop.
    // No timeline is created, so nothing animates and nothing needs pausing.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set(slides[0], { autoAlpha: 1, xPercent: 0 });
      return;
    }

    // Take over visibility from CSS. Slide 0 is on screen; the rest are parked
    // off to the right, ready to slide in.
    gsap.set(slides, { autoAlpha: 0, xPercent: shift });
    gsap.set(slides[0], { autoAlpha: 1, xPercent: 0 });

    let index = 0;

    // Paused until ScrollTrigger (and the shapes reveal, if present) say go.
    const tl = gsap.timeline({ repeat: -1, repeatDelay: hold, paused: true });

    // One step per slide, so a full repeat returns to slide 0 with the stack in
    // its starting state — no drift over long runs.
    slides.forEach(() => {
      tl.call(() => {
        const current = slides[index];
        index = (index + 1) % slides.length;
        const incoming = slides[index];

        // Outgoing: drift left and fade out.
        gsap.to(current, {
          xPercent: -shift,
          autoAlpha: 0,
          duration,
          ease: "power2.inOut",
          // Park it back on the right so it is ready for its next turn.
          onComplete: () => gsap.set(current, { xPercent: shift }),
        });

        // Incoming: slide in from the right and fade up.
        gsap.fromTo(
          incoming,
          { xPercent: shift, autoAlpha: 0 },
          { xPercent: 0, autoAlpha: 1, duration, ease: "power2.inOut" },
        );
      });

      // Hold on the new slide before the next swap. The crossfade itself is not
      // on the timeline (the tweens above run independently), so this delay must
      // cover it as well as the pause.
      tl.to({}, { duration: duration + hold });
    });

    // Only cycle while the hero is on screen.
    //
    // The gallery starts paused when a shapes reveal is present: hero-shapes.js
    // fades the whole gallery in as the last step of its intro, and starting the
    // crossfade before that finishes would fight it for the same property.
    // `revealPending` gates the first play; ScrollTrigger still owns pausing.
    const media = root.closest(".block__hero__media");
    const hasReveal = !!media?.querySelector("[data-hero-shapes]");
    let revealPending = hasReveal && !media.hasAttribute("data-hero-reveal-done");
    let inView = false;

    if (revealPending) {
      document.addEventListener("hero:revealed", (e) => {
        if (e.detail?.media !== media) return;
        revealPending = false;
        if (inView) tl.play();
      });
    }

    ScrollTrigger.create({
      trigger: root,
      start: "top bottom",
      end: "bottom top",
      onEnter: () => {
        inView = true;
        if (!revealPending) tl.play();
      },
      onLeave: () => {
        inView = false;
        tl.pause();
      },
      onEnterBack: () => {
        inView = true;
        if (!revealPending) tl.play();
      },
      onLeaveBack: () => {
        inView = false;
        tl.pause();
      },
    });

    document.addEventListener("visibilitychange", () =>
      document.hidden ? tl.pause() : tl.play(),
    );
  });
}

export { initHeroGallery };
